/**
 * meta-campaign-performance satırlarından MetaOverview ile uyumlu computedMetrics üretir.
 * (publisherPlatform + placementPosition ile placement kırılımı)
 */
export function buildMetaMetricsFromCampaignRows(metaData) {
  if (!metaData || !Array.isArray(metaData) || metaData.length === 0) {
    return {
      totalSpend: 0,
      totalImpressions: 0,
      totalClicks: 0,
      totalPurchaseConversions: 0,
      totalPurchaseValue: 0,
      averageCtr: 0,
      averageCpc: 0,
      roas: 0,
      cpa: 0,
      platforms: {
        facebook: {
          spend: 0,
          impressions: 0,
          clicks: 0,
          purchaseConversions: 0,
          purchaseValue: 0,
          ctr: 0,
          cpc: 0,
          cpa: 0,
          roas: 0,
          placements: [],
        },
        instagram: {
          spend: 0,
          impressions: 0,
          clicks: 0,
          purchaseConversions: 0,
          purchaseValue: 0,
          ctr: 0,
          cpc: 0,
          cpa: 0,
          roas: 0,
          placements: [],
        },
      },
    };
  }

  const totals = metaData.reduce(
    (acc, item) => ({
      totalSpend: acc.totalSpend + Number(item.spend || 0),
      totalImpressions: acc.totalImpressions + Number(item.impressions || 0),
      totalClicks: acc.totalClicks + Number(item.clicks || 0),
      totalPurchaseConversions:
        acc.totalPurchaseConversions + Number(item.purchaseConversions || item.conversions || 0),
      totalPurchaseValue:
        acc.totalPurchaseValue + Number(item.purchaseValue || item.conversionValue || 0),
    }),
    {
      totalSpend: 0,
      totalImpressions: 0,
      totalClicks: 0,
      totalPurchaseConversions: 0,
      totalPurchaseValue: 0,
    }
  );

  const platformData = {
    facebook: {
      spend: 0,
      impressions: 0,
      clicks: 0,
      purchaseConversions: 0,
      purchaseValue: 0,
      placements: {},
    },
    instagram: {
      spend: 0,
      impressions: 0,
      clicks: 0,
      purchaseConversions: 0,
      purchaseValue: 0,
      placements: {},
    },
  };

  metaData.forEach((item) => {
    const platform = (item.platform || item.publisherPlatform || '').toLowerCase() || 'unknown';
    const placement =
      item.placementPosition || item.platformPosition || item.platform_position || 'unknown';

    if (platform !== 'facebook' && platform !== 'instagram') return;

    platformData[platform].spend += Number(item.spend || 0);
    platformData[platform].impressions += Number(item.impressions || 0);
    platformData[platform].clicks += Number(item.clicks || 0);
    platformData[platform].purchaseConversions += Number(
      item.purchaseConversions || item.conversions || 0
    );
    platformData[platform].purchaseValue += Number(item.purchaseValue || item.conversionValue || 0);

    if (!platformData[platform].placements[placement]) {
      platformData[platform].placements[placement] = {
        name: placement,
        spend: 0,
        impressions: 0,
        clicks: 0,
        purchaseConversions: 0,
        purchaseValue: 0,
      };
    }
    const p = platformData[platform].placements[placement];
    p.spend += Number(item.spend || 0);
    p.impressions += Number(item.impressions || 0);
    p.clicks += Number(item.clicks || 0);
    p.purchaseConversions += Number(item.purchaseConversions || item.conversions || 0);
    p.purchaseValue += Number(item.purchaseValue || item.conversionValue || 0);
  });

  const platforms = {};

  ['facebook', 'instagram'].forEach((key) => {
    const fb = platformData[key];
    platforms[key] = {
      spend: fb.spend,
      impressions: fb.impressions,
      clicks: fb.clicks,
      purchaseConversions: fb.purchaseConversions,
      purchaseValue: fb.purchaseValue,
      ctr: fb.impressions > 0 ? (fb.clicks / fb.impressions) * 100 : 0,
      cpc: fb.clicks > 0 ? fb.spend / fb.clicks : 0,
      cpa: fb.purchaseConversions > 0 ? fb.spend / fb.purchaseConversions : 0,
      roas: fb.spend > 0 ? fb.purchaseValue / fb.spend : 0,
      placements: Object.values(fb.placements)
        .map((p) => ({
          name: p.name,
          spend: p.spend,
          impressions: p.impressions,
          clicks: p.clicks,
          conversions: p.purchaseConversions,
          revenue: p.purchaseValue,
          ctr: p.impressions > 0 ? (p.clicks / p.impressions) * 100 : 0,
          cpc: p.clicks > 0 ? p.spend / p.clicks : 0,
          roas: p.spend > 0 ? p.purchaseValue / p.spend : 0,
        }))
        .sort((a, b) => b.spend - a.spend),
    };
  });

  const averageCtr =
    totals.totalImpressions > 0 ? (totals.totalClicks / totals.totalImpressions) * 100 : 0;
  const averageCpc = totals.totalClicks > 0 ? totals.totalSpend / totals.totalClicks : 0;
  const roas = totals.totalSpend > 0 ? totals.totalPurchaseValue / totals.totalSpend : 0;
  const cpa =
    totals.totalPurchaseConversions > 0 ? totals.totalSpend / totals.totalPurchaseConversions : 0;

  return {
    ...totals,
    averageCtr,
    averageCpc,
    roas,
    cpa,
    platforms,
  };
}

export function processDailyTrendFromCampaignRows(campaignData) {
  if (!campaignData || !campaignData.length) return null;
  const dailyTotals = {};

  campaignData.forEach((row) => {
    const d = row.date ? new Date(row.date).toISOString().split('T')[0] : null;
    if (!d) return;
    if (!dailyTotals[d]) {
      dailyTotals[d] = { date: d, spend: 0, revenue: 0, impressions: 0, clicks: 0 };
    }
    dailyTotals[d].spend += Number(row.spend || 0);
    dailyTotals[d].revenue += Number(row.purchaseValue || row.conversionValue || 0);
    dailyTotals[d].impressions += Number(row.impressions || 0);
    dailyTotals[d].clicks += Number(row.clicks || 0);
  });

  const sortedDailyData = Object.values(dailyTotals).sort((a, b) => new Date(a.date) - new Date(b.date));
  if (sortedDailyData.length === 0) return null;

  return {
    labels: sortedDailyData.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }),
    spend: sortedDailyData.map((item) => item.spend),
    revenue: sortedDailyData.map((item) => item.revenue),
    impressions: sortedDailyData.map((item) => item.impressions),
    clicks: sortedDailyData.map((item) => item.clicks),
  };
}

export function deriveMetaCampaignTables(campaignData) {
  if (!campaignData || !campaignData.length) {
    return { campaigns: [], adSets: [], ads: [] };
  }

  const campaignSummary = {};
  campaignData.forEach((row) => {
    const campaignId = row.campaignId;
    if (!campaignSummary[campaignId]) {
      campaignSummary[campaignId] = {
        id: campaignId,
        name: row.campaignName,
        spend: 0,
        conversions: 0,
        revenue: 0,
        impressions: 0,
        clicks: 0,
        status: 'active',
      };
    }
    const c = campaignSummary[campaignId];
    c.spend += Number(row.spend || 0);
    c.conversions += Number(row.purchaseConversions || 0);
    c.revenue += Number(row.purchaseValue || 0);
    c.impressions += Number(row.impressions || 0);
    c.clicks += Number(row.clicks || 0);
  });

  const campaignsArray = Object.values(campaignSummary)
    .map((campaign) => ({
      ...campaign,
      roas: campaign.spend > 0 ? campaign.revenue / campaign.spend : 0,
      cpc: campaign.clicks > 0 ? campaign.spend / campaign.clicks : 0,
    }))
    .sort((a, b) => b.spend - a.spend);

  const adsetSummary = {};
  const adSummary = {};

  campaignData.forEach((row) => {
    const adsetId = row.adsetId;
    if (adsetId) {
      if (!adsetSummary[adsetId]) {
        adsetSummary[adsetId] = {
          id: adsetId,
          name: row.adsetName || 'Unknown AdSet',
          campaignName: row.campaignName,
          spend: 0,
          conversions: 0,
          revenue: 0,
          impressions: 0,
          clicks: 0,
          status: 'active',
        };
      }
      const a = adsetSummary[adsetId];
      a.spend += Number(row.spend || 0);
      a.conversions += Number(row.purchaseConversions || 0);
      a.revenue += Number(row.purchaseValue || 0);
      a.impressions += Number(row.impressions || 0);
      a.clicks += Number(row.clicks || 0);
    }

    const adId = row.adId;
    if (adId) {
      if (!adSummary[adId]) {
        adSummary[adId] = {
          id: adId,
          name: row.adName || 'Unknown Ad',
          campaignName: row.campaignName,
          adsetName: row.adsetName || 'Unknown AdSet',
          spend: 0,
          conversions: 0,
          revenue: 0,
          impressions: 0,
          clicks: 0,
          status: 'active',
        };
      }
      const ad = adSummary[adId];
      ad.spend += Number(row.spend || 0);
      ad.conversions += Number(row.purchaseConversions || 0);
      ad.revenue += Number(row.purchaseValue || 0);
      ad.impressions += Number(row.impressions || 0);
      ad.clicks += Number(row.clicks || 0);
    }
  });

  const adsetsArray = Object.values(adsetSummary)
    .map((adset) => ({
      ...adset,
      roas: adset.spend > 0 ? adset.revenue / adset.spend : 0,
      cpc: adset.clicks > 0 ? adset.spend / adset.clicks : 0,
    }))
    .sort((a, b) => b.spend - a.spend);

  const adsArray = Object.values(adSummary)
    .map((ad) => ({
      ...ad,
      roas: ad.spend > 0 ? ad.revenue / ad.spend : 0,
      cpc: ad.clicks > 0 ? ad.spend / ad.clicks : 0,
    }))
    .sort((a, b) => b.spend - a.spend);

  return { campaigns: campaignsArray, adSets: adsetsArray, ads: adsArray };
}
